import { useCallback, useMemo, useState } from "react";
import type { SchemaElement } from "../../../api/restaurantSchema";

/**
 * Изборът на маси в мрежата. Клик върху плочка избира само нея (повторен клик
 * върху единствената избрана я маха); чекбоксът добавя/маха. `tables` са
 * масите на активния регион: изтрити/преместени (или друг регион) изпадат от
 * избора сами — `selectedTables` се смята от тях, не от запомнените id-та.
 * Скрити от филтъра/търсенето маси не остават избрани, защото страницата чисти
 * избора при смяна на чипа/търсенето (а избраната маса остава видима).
 */
export const useTablesSelection = (tables: SchemaElement[]) => {
  const [selectedIds, setSelectedIds] = useState<Set<number>>(() => new Set());

  const selectedTables = useMemo(
    () => tables.filter((table) => selectedIds.has(table.id)),
    [tables, selectedIds],
  );
  // Само id-тата на масите, които още са тук (за чекбоксовете).
  const presentIds = useMemo(() => new Set(selectedTables.map((table) => table.id)), [selectedTables]);

  const selectOnly = useCallback((table: SchemaElement) => {
    setSelectedIds((current) =>
      current.size === 1 && current.has(table.id) ? new Set() : new Set([table.id]),
    );
  }, []);

  const toggle = useCallback((table: SchemaElement) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(table.id)) next.delete(table.id);
      else next.add(table.id);
      return next;
    });
  }, []);

  const selectMany = useCallback((many: SchemaElement[]) => {
    setSelectedIds(new Set(many.map((table) => table.id)));
  }, []);

  const clear = useCallback(() => setSelectedIds(new Set()), []);

  return { selectedIds: presentIds, selectedTables, selectOnly, toggle, selectMany, clear, setSelectedIds };
};
